import {
    SelectionChangedEvent,
    GridReadyEvent,
    RowNode,
    ModelUpdatedEvent,
} from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { BaseAgGrid } from "../grid/BaseAgGrid";
import { AgGridReact } from "ag-grid-react";
import { useSiteAnalyzeOffSiteGridColumns } from "./SiteAnalyzeOffSiteGridColumns/useSiteAnalyzeOffSiteGridColumns";
import Pacman from "../layout/Pacman";
import { OrderOffSiteGrid } from "src/app/types/orders/offSite";
import { TOffSiteOrderType } from "src/app/types/orders/order";
import {
    cleanUpQuery,
    getApplyColumnState,
    getFiltersFromQueryString,
    getQueryParamsFromObjects,
} from "../grid/GridHelpers";
import { useLocation, useSearchParams } from "react-router-dom";
import { SimpleEntityGridActionCellButton } from "../grid/CellRenderer/SimpleEntityGridActionCell/SimpleEntityGridActionCell";
import { NetlinkingStepModalFormContainer, SiloStepModalFormContainer } from "src/app/containers";
import IndexMeFormBodyContainer from "src/app/containers/IndexMeFormBody/IndexMeFormBodyContainer";
import SendTextOrderFormContainerWithChecked from "src/app/pages/ContentManagement/OnlineGrid/SendTextOrderFormContainerWithChecked";
import { OrderTransport } from "src/app/transports/order";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";

export interface ISiteAnalyzeOffSiteGrid {
    orderType: TOffSiteOrderType;
    rowData?: Array<OrderOffSiteGrid>;
    refreshRowData?(): void;
    refreshHistoryData?(): void;
    setSelectedRows?: React.Dispatch<React.SetStateAction<any[]>>;
    onSelectionChanged?: (event: any) => void;
    gridRef?: React.RefObject<any>;
    isVisible?: boolean;
    historyByMoneySiteOffSite?: Array<OrderOffSiteGrid>;
    isHistoryView?: boolean;
    loading?: boolean;
}
const SiteAnalyzeOffSiteGrid = (props: ISiteAnalyzeOffSiteGrid) => {
    const {
        rowData,
        setSelectedRows = () => {},
        refreshRowData,
        gridRef: externalGridRef,
        orderType,
        isVisible,
    } = props;

    //refs for Filters
    const gridRef = useRef<AgGridReact>(null);
    const oldQuery = useRef("");
    const newQuery = useRef("");

    const location = useLocation();
    const [searchParams] = useSearchParams();

    const localGridRef = useRef<AgGridReact<any>>(null);
    const effectiveGridRef = externalGridRef || localGridRef;

    const { formatMessage } = useIntl();

    const [showModal, setShowModal] = useState<SimpleEntityGridActionCellButton>("");
    const [entity, setEntity] = useState<{
        orderId: number;
        id: number;
        keywords?: string[];
    }>();

    const handleDisable = (data: OrderOffSiteGrid) => {
        try {
            OrderTransport.toggleActive({ id: data.id });
            refreshRowData && refreshRowData();
        } catch (_) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        }
    };

    const handleActionModals = (
        modal: SimpleEntityGridActionCellButton,
        data: OrderOffSiteGrid
    ) => {
        if (modal === "disable") {
            handleDisable(data);
        }

        setShowModal(modal);
        setEntity({
            orderId: data.id,
            id: data.id,
            keywords: data.keywords || [],
        });
    };

    const handleClose = () => setShowModal("");

    const gridColumns = useSiteAnalyzeOffSiteGridColumns(orderType, handleActionModals);

    const onSelectionChanged = (event: SelectionChangedEvent) => {
        const selectedRows = event.api.getSelectedRows();
        setSelectedRows(selectedRows);
        if (props.onSelectionChanged) {
            props.onSelectionChanged(event);
        }
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            const gridApi = params.api;
            const columnApi = params.columnApi;

            gridApi.sizeColumnsToFit();

            const query = cleanUpQuery(location.search);
            const { filters, sort } = getFiltersFromQueryString(query);

            delete filters?.moneySiteId;

            const urlParams = new URLSearchParams(window.location.search);
            const filterParam = urlParams.get("filter");

            const currentFilters = !filters ? {} : filters;

            gridApi.setFilterModel(currentFilters);

            if (sort?.length) {
                columnApi.applyColumnState({
                    state: getApplyColumnState(sort),
                });
            }

            if (filterParam) {
                try {
                    const filterModel = JSON.parse(filterParam);
                    gridApi.setFilterModel(filterModel);
                } catch (error) {
                    throw new Error("Failed to apply filters from the URL");
                }
            }

            //init filters
            window.addEventListener("resize", function () {
                if (effectiveGridRef?.current?.api) {
                    setTimeout(function () {
                        effectiveGridRef?.current?.api.sizeColumnsToFit();
                    });
                }
            });

            const moneySiteId = searchParams.get("moneySiteId");

            if (oldQuery.current !== query) {
                oldQuery.current = query;
                window.history.replaceState(
                    null,
                    "",
                    `?moneySiteId=${moneySiteId}${query ? `&${query}` : ""}`
                );
            }

            gridApi.setRowData(rowData || []);
        },
        [rowData]
    );

    const onModelUpdate = useCallback((event: ModelUpdatedEvent) => {
        const query = getQueryParamsFromObjects(
            event.api.getFilterModel(),
            event.columnApi.getColumnState()
        );
        const moneySiteId = searchParams.get("moneySiteId");

        if (oldQuery.current !== query) {
            //set old query to prevent extra navigation push
            oldQuery.current = query;
            window.history.replaceState(
                null,
                "",
                `?moneySiteId=${moneySiteId}${query ? `&${query}` : ""}`
            );
        }
    }, []);

    const doesExternalFilterPass = useCallback((node: RowNode): boolean => {
        if (node.data) {
            return !!node.data.active;
        }
        return true;
    }, []);

    useEffect(() => {
        const { filters, sort } = getFiltersFromQueryString(location.search);

        delete filters?.moneySiteId;

        newQuery.current = cleanUpQuery(location.search);
        if (gridRef?.current?.api && oldQuery.current !== newQuery.current) {
            oldQuery.current = newQuery.current;
            gridRef.current.api.setFilterModel(filters);
            gridRef.current.columnApi.applyColumnState({
                state: getApplyColumnState(sort ?? []),
            });
        }
    }, [location.search]);

    const loadingPacMen = () => (
        <div className="d-flex">
            <Pacman />
        </div>
    );

    return (
        <>
            <div className="ag-theme-balham" style={{ height: isVisible ? "100%" : "900px" }}>
                <BaseAgGrid
                    rowSelection="multiple"
                    listHandler={() => {}}
                    loadingOverlayComponent={loadingPacMen}
                    onSelectionChanged={onSelectionChanged}
                    gridRef={effectiveGridRef}
                    rowData={rowData}
                    columnDefs={gridColumns}
                    isExternalFilterPresent={() => true}
                    onModelUpdated={onModelUpdate}
                    doesExternalFilterPass={doesExternalFilterPass}
                    onGridReady={onGridReady}
                />
            </div>
            {entity &&
                {
                    sendText: (
                        <SendTextOrderFormContainerWithChecked
                            entity={entity}
                            handleClose={handleClose}
                        />
                    ),
                    netlinking: (
                        <NetlinkingStepModalFormContainer
                            entity={entity}
                            handleClose={handleClose}
                        />
                    ),
                    silo: <SiloStepModalFormContainer entity={entity} handleClose={handleClose} />,
                    createCampaign: <></>,
                    createPopcashCampaign: <></>,
                    indexMe: <IndexMeFormBodyContainer entity={entity} handleClose={handleClose} />,
                    disable: <></>,
                    enable: <></>,
                    edit: <></>,
                    editPersona: <></>,
                    wordpressAccess: <></>,
                    viewText: <></>,
                    chooseRD: <></>,
                    waiting: <></>,
                    addUrl: <></>,
                    disableWithConfirmation: <></>,
                    verified: <></>,
                    approve: <></>,
                    orderText: <></>,
                    addAITitleAndPlan: <></>,
                    addAITitle: <></>,
                    addAIPlan: <></>,
                    removeRD: <></>,
                    plannings: <></>,
                    AIGuide: <></>,
                    "": <></>,
                }[showModal]}
        </>
    );
};

export default SiteAnalyzeOffSiteGrid;
